/* eslint-disable @typescript-eslint/naming-convention */
import * as readerConfigActions from '@mhe/reader/core/state/configuration/configuration.actions';
import * as errorsActions from '@mhe/reader/global-store/errors/errors.actions';
import * as readerSessionActions from '@mhe/reader/state/session/session.actions';
import { LtiParams } from '@mhe/reader/models';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { LtiService } from './lti.service';
import * as configActions from '@mhe/reader/state/config/config.actions';
import { mapLtiToRuntimeConfig } from './lti-to-runtime-config';
import { ReaderTokenService } from '@mhe/reader/tokens';
import { PendoService } from '@mhe/reader/features/analytics';
import { firstValueFrom } from 'rxjs';
import { EpubFactoryApiService } from '@mhe/reader/core/epub-factory-api/epub-factory-api.service';

const errorRoutes = ['0', '401', '403', '404', '500'];

export function loadLtiParameters(
  store: Store,
  ltiService: LtiService,
  router: Router,
  tokenService: ReaderTokenService,
  pendo: PendoService,
  epubFactoryApiService: EpubFactoryApiService,
): () => Promise<void> {
  // Since we change the way we handle the errors from epub factory
  // We don't want to make the lti request if the page is inside the error routes
  if (errorRoutes.includes(window.location.pathname.replace('/', ''))) {
    return async() => await Promise.resolve();
  }
  return async() => {
    await ltiService.ltiParameters$
      .toPromise()
      .then(async(params: LtiParams) => {
        await ltiToStore(params);
        console.log('LTI parameters loaded successfully', params);

        pendo.injectSnippet(params, () => {
          pendo.initialize(params);
        });

        // EPR-8279 block rendering Aleks content if the user copies a link and opens it in a new tab
        if (params.oauth_consumer_key === 'aleks' && !document.referrer.includes('.aleks.com')) {
          await router.navigateByUrl('401', { skipLocationChange: true });
          return;
        }

        await seedSessionToken(params);
      })
      .catch((error) => {
        void router.navigateByUrl(error.status?.toString(), { skipLocationChange: true });
      });
  };

  /** sub LTI dependent functions */
  async function ltiToStore(lti: LtiParams): Promise<void> {
    store.dispatch(readerConfigActions.SetPartialRuntimeFeatures(mapLtiToRuntimeConfig(lti)));

    const {
      context_id, custom_epub_url, custom_epub_factory_access_token,
      custom_epub_version, custom_epub_release,
    } = lti;

    // Check if the new epub factory token is present
    if (custom_epub_factory_access_token && context_id) {
      try {
        // Fetch the publish.json from epub-factory
        const publishJson = await firstValueFrom(
          epubFactoryApiService.fetchPublishJson(context_id, custom_epub_factory_access_token,
            custom_epub_version, custom_epub_release),
        );

        // Calculate the epub launch URL from publish.json
        const epubLaunchUrl = await firstValueFrom(
          epubFactoryApiService.calculateEpubLaunchUrl(publishJson, lti),
        );

        // Store the calculated epub URL
        const epub = { [context_id]: epubLaunchUrl };
        store.dispatch(configActions.setEpubDictionary({ epub }));
      } catch (error: unknown) {
        const safeErrorInfo =
          typeof error === 'object' && error !== null
            ? {
                status: (error as { status?: unknown }).status,
                statusText: (error as { statusText?: unknown }).statusText,
                message: (error as { message?: unknown }).message,
                name: (error as { name?: unknown }).name,
              }
            : { message: String(error) };

        console.error('Failed to fetch publish.json from epub-factory', safeErrorInfo);
        // Fallback to the standard workflow
        const epub = { [context_id]: custom_epub_url };
        store.dispatch(configActions.setEpubDictionary({ epub }));
      }
    } else {
      // Use the standard workflow
      const epub = { [context_id]: custom_epub_url };
      store.dispatch(configActions.setEpubDictionary({ epub }));
    }
  }

  async function seedSessionToken({
    custom_readerx_token: accessToken,
    custom_readerx_session_disabled: sessionDisabled,
  }: LtiParams): Promise<void> {
    // start session for reader APP
    store.dispatch(readerSessionActions.SessionStart({ session: { accessToken } }));

    if (!accessToken) {
      // let reader COMPONENT know session has already ended
      // to avoid an unnecessary http request being made
      store.dispatch(errorsActions.sessionEnded());
    } else if (sessionDisabled !== 'true') {
      // call global-session on app init to check
      // whether token/session is still good so we
      // can fail early if needed
      await firstValueFrom(tokenService.refreshToken())
        .then((token) => tokenService.setToken(token))
        .catch(() => {
          // let reader COMPONENT know session has already ended
          store.dispatch(errorsActions.sessionEnded());
        });
    }
  }
}

export * from './lti-to-runtime-config';
export * from './lti.service';
